import { Sparkles } from 'lucide-react';
import { useState } from 'react';
import Button from '../Button.jsx';
import { DEFAULT_AI_PROMPT_PLACEHOLDER } from './richTextEditor.constants.js';

/**
 * Panel below the toolbar (when the AI icon is open):
 * - **Prompt row + Apply**: only if `onAiPromptApply` is set. **Apply** submits the input text
 *   (free prompt), not the quick-action chips.
 * - **Chips**: each button has an internal `onClick`; by default it calls `onAiAction(id, payload)`.
 *   Optional: `action.onClick(payload)` on the item — if present, it **replaces** the global callback.
 */
const RichTextEditorAiBar = ({
  aiActions = [],
  onAiAction,
  onAiPromptApply,
  aiPromptPlaceholder = DEFAULT_AI_PROMPT_PLACEHOLDER,
  getSelectionContext,
  getEditorApi,
  disabled = false,
  readOnly = false,
}) => {
  const [prompt, setPrompt] = useState('');
  const isLocked = disabled || readOnly;
  const showPrompt = typeof onAiPromptApply === 'function';
  const hasAiActions = aiActions.length > 0;
  const canApplyPrompt = !isLocked && prompt.trim().length > 0;

  if (!showPrompt && !hasAiActions) return null;

  return (
    <div className="flex flex-col gap-2.5 border-b border-edge bg-surface-muted p-2.5">
      {showPrompt ? (
        <div className="flex items-center gap-2">
          <div className="inline-flex min-h-8 flex-1 items-center gap-2 rounded-md border border-edge-strong bg-surface px-2.5 dark:border-edge">
            <Sparkles size={16} className="text-brand-500" />
            <input
              value={prompt}
              disabled={isLocked}
              placeholder={aiPromptPlaceholder}
              className="w-full border-0 bg-transparent text-[12px] text-content outline-none"
              onChange={(event) => setPrompt(event.target.value)}
            />
          </div>
          <Button
            type="button"
            size="sm"
            variant="primary"
            disabled={!canApplyPrompt}
            className="!h-8 !px-4 !text-[12px] !font-semibold"
            onClick={() => {
              const trimmedPrompt = prompt.trim();
              if (!trimmedPrompt) return;
              onAiPromptApply?.({
                prompt: trimmedPrompt,
                context: getSelectionContext(),
                editorApi: getEditorApi?.(),
              });
            }}
          >
            Apply
          </Button>
        </div>
      ) : null}

      {hasAiActions ? (
        <div className="flex flex-wrap gap-2">
          {aiActions.map((action) => {
            const Icon = action.icon;
            return (
              <Button
                key={action.id}
                type="button"
                size="sm"
                variant="secondary"
                pill
                disabled={isLocked || Boolean(action.disabled)}
                className="!h-8 !px-3 !text-[12px] !font-medium"
                onClick={() => {
                  const payload = {
                    action,
                    context: getSelectionContext(),
                    editorApi: getEditorApi?.(),
                  };
                  if (typeof action.onClick === 'function') {
                    action.onClick(payload);
                    return;
                  }
                  onAiAction?.(action.id, payload);
                }}
              >
                {Icon ? <Icon size={14} /> : null}
                <span>{action.label}</span>
              </Button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default RichTextEditorAiBar;
